import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Rocket, Palette, DollarSign, Star, Users, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { initiatePayment } from '../services/payment';

export function ProductIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = location.state?.userProfile;

  const handleBack = () => {
    navigate(-1);
  };

  const handleJoin = async (plan: 'basic' | 'vip') => {
    try {
      await initiatePayment({
        plan,
        amount: plan === 'basic' ? 5999 : 24999,
        userProfile: userProfile || {
          industry: { primary: 'tech' },
          useCases: [],
          experience: { years: 0, roles: ['Learner'] },
          interests: ['AI'],
          learningStyle: 'practical',
          goals: ['Improve knowledge']
        }
      });
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      // 可以添加错误提示UI
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#2a2a4e] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#1a1a2e]/80 backdrop-blur-md z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo />
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回评估
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-24 pb-16">
        {/* Hero Section */}
        <motion.section 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            AIGC-DAO 产品服务介绍
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            结合 AI人工智能 与 Web3技术 的先锋生态组织，为企业和个人提供一站式AI+创新数字解决方案
          </p>
        </motion.section>

        {/* Core Products */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* AI Enterprise Training */}
          <motion.div
            className="p-6 bg-white/5 rounded-xl border border-white/10"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Brain className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold">AI企业培训与工具包</h3>
            </div>
            <ul className="space-y-2 text-gray-300">
              <li>• LabbbFun AI工具：一站式AI助手</li>
              <li>• 定制化培训：从基础到工作流优化</li>
              <li>• 私有化部署：确保数据安全</li>
            </ul>
          </motion.div>

          {/* Digital Marketing */}
          <motion.div
            className="p-6 bg-white/5 rounded-xl border border-white/10"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Rocket className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold">AI+Web3数字营销全案</h3>
            </div>
            <ul className="space-y-2 text-gray-300">
              <li>• AIGC内容营销：品牌IP生成</li>
              <li>• Web3整合策略：RWA资产发行</li>
              <li>• 社区运营：GameFi活动策划</li>
            </ul>
          </motion.div>

          {/* Digital Content Creation */}
          <motion.div
            className="p-6 bg-white/5 rounded-xl border border-white/10"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Palette className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold">AIGC数字内容创作</h3>
            </div>
            <ul className="space-y-2 text-gray-300">
              <li>• AI虚拟数字人：多语言支持</li>
              <li>• AI商业设计：电商海报生成</li>
              <li>• 3D资产：盲盒、室内效果图</li>
            </ul>
          </motion.div>

          {/* RWA Asset Services */}
          <motion.div
            className="p-6 bg-white/5 rounded-xl border border-white/10"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-semibold">RWA资产服务</h3>
            </div>
            <ul className="space-y-2 text-gray-300">
              <li>• 资产代币化：房地产、版权收益</li>
              <li>• 稳定分红：年化8-12%</li>
              <li>• 智能合约：风险评估模型</li>
            </ul>
          </motion.div>
        </section>

        {/* Membership Plans */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            会员权益
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Basic Plan */}
            <motion.div
              className="p-6 bg-white/5 rounded-xl border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Star className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">基础会员</h3>
                  <p className="text-gray-400">¥5,999</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-blue-400">•</span>
                  LabbbFun高级工具包（1年）
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-blue-400">•</span>
                  AI企业培训课程（3节）
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-blue-400">•</span>
                  DA-RWA分红凭证（5张，年化8%）
                </li>
              </ul>
              <button
                onClick={() => handleJoin('basic')}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:opacity-90 rounded-lg transition-colors"
              >
                立即加入
              </button>
            </motion.div>

            {/* VIP Plan */}
            <motion.div
              className="p-6 bg-gradient-to-r from-[#2a2a4e] to-[#3a3a6e] rounded-xl border border-purple-500/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">VIP会员</h3>
                  <p className="text-gray-400">¥24,999</p>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-purple-400">•</span>
                  包含基础会员所有权益
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-purple-400">•</span>
                  1v1品牌咨询（3次）
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-purple-400">•</span>
                  RWA优先认购权
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <span className="text-purple-400">•</span>
                  联盟分润15%
                </li>
              </ul>
              <button
                onClick={() => handleJoin('vip')}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:opacity-90 rounded-lg transition-colors"
              >
                立即加入
              </button>
            </motion.div>
          </div>
        </section>

        {/* Success Cases */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            成功案例
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              className="p-6 bg-white/5 rounded-xl border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <h4 className="font-semibold mb-2">电商品牌优化</h4>
              <p className="text-gray-300">
                通过AIGC生成500+张产品图，节省设计成本12万元/月
              </p>
            </motion.div>
            <motion.div
              className="p-6 bg-white/5 rounded-xl border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <h4 className="font-semibold mb-2">房地产RWA</h4>
              <p className="text-gray-300">
                香港地产商发行2000万U房地产RWA，3周售罄，投资者年化收益9.5%
              </p>
            </motion.div>
            <motion.div
              className="p-6 bg-white/5 rounded-xl border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <h4 className="font-semibold mb-2">数字人教育</h4>
              <p className="text-gray-300">
                "抗战老兵数字人"项目获教育局资助，历史教育效率提升300%
              </p>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  );
}